import React, { useEffect, useState } from "react";
import { ArrowRight, PlayCircle, X } from "lucide-react";
import { useLeadModal } from "./LeadModalContext";

const VIDEO_ID = "TSf32I2m1VE";
const POSTER =
  "https://customer-assets.emergentagent.com/job_guest-intelligence/artifacts/0j51d5sb_welcome-stori.jpg";

const HEADLINE_LINES = [
  { text: "The AI selfie cam", accent: false },
  { text: "for every event and venue.", accent: true },
];

export default function Hero() {
  const [videoOpen, setVideoOpen] = useState(false);
  const { openModal } = useLeadModal();

  useEffect(() => {
    if (!videoOpen) return;
    const onKey = (e) => e.key === "Escape" && setVideoOpen(false);
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = "";
      window.removeEventListener("keydown", onKey);
    };
  }, [videoOpen]);

  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative w-full min-h-[100svh] overflow-hidden flex flex-col items-start justify-center pt-24 pb-20"
    >
      {/* Cinematic poster background — image weighted to the right; left masked dark for copy legibility */}
      <div className="absolute inset-0 z-0 pointer-events-none">
        <img
          src={POSTER}
          alt=""
          aria-hidden="true"
          className="absolute inset-0 w-full h-full object-cover"
          style={{ objectPosition: "75% 30%", filter: "saturate(1) contrast(1.05)" }}
        />
        {/* Horizontal mask: solid black on the left → transparent on the right */}
        <div className="absolute inset-0 bg-gradient-to-r from-stori-bg via-stori-bg/80 to-stori-bg/5" />
        {/* Solid block for the leftmost portion guaranteeing copy contrast */}
        <div className="absolute inset-y-0 left-0 w-[40%] bg-stori-bg" />
        <div className="absolute inset-y-0 left-[40%] w-[18%] bg-gradient-to-r from-stori-bg to-transparent" />
        {/* Bottom fade to blend into next section */}
        <div className="absolute inset-x-0 bottom-0 h-40 bg-gradient-to-t from-stori-bg to-transparent" />
        {/* Subtle ambient cyan glow (kept on the left so it sits behind copy) */}
        <div className="absolute -top-20 -left-20 w-[600px] h-[600px] bg-stori-cyan/12 rounded-full blur-[140px]" />
      </div>

      {/* Content */}
      <div className="relative z-10 max-w-[1400px] w-full mx-auto px-6 lg:px-10">
        <div className="max-w-4xl">
          <h1
            data-testid="hero-headline"
            className="font-heading text-[clamp(2.5rem,6.5vw,5.5rem)] leading-[1.02] font-semibold tracking-tight text-white"
          >
            {HEADLINE_LINES.map((line, i) => (
              <span
                key={i}
                className={`block fade-up ${line.accent ? "text-stori-cyan cyan-text-glow" : ""}`}
                style={{ animationDelay: `${i * 90}ms` }}
              >
                {line.text}
              </span>
            ))}
          </h1>

          <p
            data-testid="hero-subheadline"
            className="mt-8 text-lg sm:text-xl text-stori-muted max-w-2xl leading-relaxed"
          >
            Capture branded guest photos, grow your audience, and automate your
            marketing follow-up — built for events, hotels, restaurants, retail,
            and brand activations.
          </p>

          <div className="mt-10 flex flex-wrap items-center gap-3">
            <button
              type="button"
              onClick={() => openModal("demo_request")}
              data-testid="hero-cta-book-demo"
              className="group inline-flex items-center gap-2 bg-stori-cyan text-black font-medium px-6 py-3.5 rounded-md hover:bg-stori-cyanHover transition-all hover:translate-y-[-1px]"
            >
              Book a demo
              <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5" />
            </button>
            <button
              type="button"
              onClick={() => setVideoOpen(true)}
              data-testid="hero-cta-see-action"
              className="group inline-flex items-center gap-2 border border-stori-cyan/60 text-stori-cyan px-6 py-3.5 rounded-md hover:bg-stori-cyan/10 transition-colors"
            >
              <span className="relative inline-flex items-center justify-center w-5 h-5">
                <span className="absolute inset-0 rounded-full bg-stori-cyan/30 pulse-ring" />
                <PlayCircle className="w-4 h-4 play-pulse relative" fill="currentColor" stroke="black" />
              </span>
              See it in action
            </button>
            <button
              type="button"
              onClick={() => openModal("trial_signup")}
              data-testid="hero-cta-free-trial"
              className="text-sm text-stori-muted hover:text-white px-3 py-2 underline-offset-4 hover:underline"
            >
              Get started for free →
            </button>
          </div>
        </div>
      </div>

      {/* Scroll indicator */}
      <div className="absolute bottom-6 left-1/2 -translate-x-1/2 z-10 hidden md:flex flex-col items-center gap-2 text-stori-muted">
        <span className="font-mono text-[10px] uppercase tracking-[0.3em]">scroll</span>
        <span className="block w-px h-10 bg-gradient-to-b from-stori-cyan to-transparent" />
      </div>

      {/* Video Modal */}
      {videoOpen && (
        <div
          data-testid="video-modal"
          className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/85 backdrop-blur-md"
          onClick={() => setVideoOpen(false)}
        >
          <button
            aria-label="Close video"
            data-testid="video-modal-close"
            className="absolute top-6 right-6 w-11 h-11 rounded-full border border-stori-border text-white hover:border-stori-cyan hover:text-stori-cyan transition-colors flex items-center justify-center"
            onClick={() => setVideoOpen(false)}
          >
            <X className="w-5 h-5" />
          </button>
          <div
            className="w-full max-w-5xl aspect-video rounded-xl overflow-hidden border border-stori-border bg-black"
            onClick={(e) => e.stopPropagation()}
          >
            <iframe
              title="Stori sizzle reel"
              src={`https://www.youtube.com/embed/${VIDEO_ID}?autoplay=1&rel=0&modestbranding=1`}
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              className="w-full h-full"
              style={{ border: 0 }}
            />
          </div>
        </div>
      )}
    </section>
  );
}
